import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Avatar, Button, Spinner, Textarea } from '@nextui-org/react';
import {
  ArrowLeft,
  ChevronDown,
  ImageOff,
  MessageCircle,
  Reply,
  Send,
  Target,
  Trash2,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import communityService, {
  PublicDartSetup,
  SetupComment,
} from '../services/communityService';

const ACCENT = '#3fced1';

const SetupDetailPage: React.FC = () => {
  const { setupId } = useParams<{ setupId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState<PublicDartSetup | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [comments, setComments] = useState<SetupComment[]>([]);
  const [totalComments, setTotalComments] = useState(0);
  const [totalAllComments, setTotalAllComments] = useState(0);
  const [loadingComments, setLoadingComments] = useState(true);

  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState<SetupComment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  // Zusätzlich nachgeladene Antworten pro Kommentar-ID
  const [extraReplies, setExtraReplies] = useState<Record<string, SetupComment[]>>({});
  const [loadingRepliesFor, setLoadingRepliesFor] = useState<string | null>(null);

  const loadSetup = useCallback(async () => {
    if (!setupId) return;
    setLoading(true);
    setNotFound(false);
    const result = await communityService.getSetupById(setupId);
    if (!result) {
      setNotFound(true);
    }
    setSetup(result);
    setLoading(false);
  }, [setupId]);

  const loadComments = useCallback(async () => {
    if (!setupId) return;
    setLoadingComments(true);
    const result = await communityService.getSetupComments(setupId, { limit: 50 });
    setComments(result.comments);
    setTotalComments(result.total);
    setTotalAllComments(result.totalAll);
    setLoadingComments(false);
  }, [setupId]);

  useEffect(() => {
    loadSetup();
    loadComments();
    window.scrollTo({ top: 0 });
  }, [loadSetup, loadComments]);

  const submitComment = async () => {
    if (!setupId) return;
    const content = commentText.trim();
    if (!content) return;
    setIsSubmitting(true);
    setCommentError(null);
    try {
      await communityService.addSetupComment(setupId, content, replyTo?.id ?? null);
      setCommentText('');
      setReplyTo(null);
      setExtraReplies({});
      await loadComments();
    } catch (err: any) {
      setCommentError(
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
          err?.response?.data?.message ||
          'Kommentar konnte nicht gespeichert werden.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteComment = async (comment: SetupComment) => {
    if (!setupId || !comment.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Kommentar wirklich löschen?')) return;
    setDeletingCommentId(comment.id);
    setCommentError(null);
    try {
      await communityService.deleteSetupComment(setupId, comment.id);
      setExtraReplies({});
      await loadComments();
    } catch (err: any) {
      setCommentError('Kommentar konnte nicht gelöscht werden.');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const loadMoreReplies = async (comment: SetupComment) => {
    if (!comment.id || loadingRepliesFor) return;
    setLoadingRepliesFor(comment.id);
    const result = await communityService.getCommentReplies(comment.id, { limit: 50 });
    setExtraReplies((prev) => ({ ...prev, [comment.id]: result.replies }));
    setLoadingRepliesFor(null);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (notFound || !setup) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Target className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Setup nicht gefunden</h2>
          <p className="text-zinc-400 mb-6 text-sm">
            Das gesuchte Setup existiert nicht oder ist nicht mehr öffentlich.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
        </div>
      </div>
    );
  }

  const imageUrl = communityService.buildImageUrl(setup.image);
  const userAvatarUrl = communityService.buildThumbnailUrl(setup.user?.image, 100);
  const createdAtLabel = communityService.formatRelativeTime(setup.createdAt);
  const commentCount = setup.commentCount ?? totalAllComments;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Helmet>
        <title>{setup.title} - Dart-Setup | DartCloud</title>
        <meta
          name="description"
          content={`Dart-Setup "${setup.title}"${
            setup.user?.username ? ` von ${setup.user.username}` : ''
          } in der DartCloud Community ansehen.`}
        />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Zurück */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Setup-Bild */}
          <div className="rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900 aspect-square flex items-center justify-center">
            {imageUrl ? (
              <img src={imageUrl} alt={setup.title} className="w-full h-full object-cover" />
            ) : (
              <ImageOff size={48} className="text-zinc-600" />
            )}
          </div>

          {/* Setup-Infos */}
          <div className="flex flex-col">
            <h1 className="text-2xl sm:text-3xl font-bold">{setup.title}</h1>
            {setup.subtitle && <p className="mt-2 text-lg text-zinc-400">{setup.subtitle}</p>}
            {setup.description && (
              <p className="mt-4 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {setup.description}
              </p>
            )}

            <div className="mt-4 flex items-center gap-3 text-xs text-zinc-500">
              {createdAtLabel && <span>Erstellt {createdAtLabel}</span>}
              <span className="inline-flex items-center gap-1">
                <MessageCircle className="w-3.5 h-3.5" />
                {commentCount} {commentCount === 1 ? 'Kommentar' : 'Kommentare'}
              </span>
            </div>

            {/* User-Info */}
            {setup.user?.username && (
              <RouterLink
                to={`/spieler/${encodeURIComponent(setup.user.username)}`}
                className="mt-6 flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 hover:bg-zinc-900 hover:border-zinc-700 p-3 transition-colors"
              >
                <Avatar
                  src={userAvatarUrl ?? undefined}
                  showFallback
                  fallback={<UserIcon size={18} className="text-zinc-500" />}
                  className="h-10 w-10 bg-zinc-800"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: ACCENT }}>
                    {setup.user.username}
                  </p>
                  <p className="text-xs text-zinc-500">Zum Spielerprofil</p>
                </div>
              </RouterLink>
            )}
          </div>
        </div>

        {/* Kommentare */}
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-5 h-5" style={{ color: ACCENT }} />
            <h2 className="text-lg font-semibold">
              Kommentare {totalComments > 0 && `(${totalAllComments})`}
            </h2>
          </div>

          {/* Kommentar schreiben */}
          {isAuthenticated ? (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4 mb-6">
              {replyTo && (
                <div className="flex items-center justify-between gap-2 mb-2 rounded-lg bg-zinc-800/70 px-3 py-2 text-xs text-zinc-300">
                  <span className="truncate">
                    Antwort an <span className="font-semibold">{replyTo.authorName}</span>:{' '}
                    {replyTo.content}
                  </span>
                  <button
                    type="button"
                    onClick={() => setReplyTo(null)}
                    className="text-zinc-400 hover:text-white shrink-0"
                    aria-label="Antwort abbrechen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <Textarea
                placeholder={
                  replyTo ? 'Schreibe deine Antwort …' : 'Schreibe einen Kommentar …'
                }
                value={commentText}
                onValueChange={setCommentText}
                maxLength={500}
                minRows={2}
                variant="bordered"
                classNames={{
                  inputWrapper:
                    'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]',
                  input: 'text-white placeholder:text-gray-500',
                }}
              />
              {commentError && <p className="mt-2 text-xs text-red-400">{commentError}</p>}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-zinc-500">{commentText.length} / 500</span>
                <Button
                  size="sm"
                  className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                  startContent={<Send size={14} />}
                  isLoading={isSubmitting}
                  isDisabled={!commentText.trim()}
                  onPress={submitComment}
                >
                  {replyTo ? 'Antworten' : 'Kommentieren'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4 mb-6 text-sm text-zinc-400">
              <RouterLink to="/login" className="text-[#3fced1] hover:text-[#35b5b8] font-medium">
                Melde dich an
              </RouterLink>
              , um einen Kommentar zu schreiben.
            </div>
          )}

          {/* Kommentar-Liste */}
          {loadingComments ? (
            <div className="flex justify-center py-10">
              <Spinner color="primary" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-sm">
              Noch keine Kommentare. Sei der Erste!
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {comments.map((comment) => {
                const replies = extraReplies[comment.id] ?? comment.replies ?? [];
                const hasMoreReplies =
                  !extraReplies[comment.id] && comment.replyCount > replies.length;
                return (
                  <div key={comment.id}>
                    <CommentCard
                      comment={comment}
                      canDelete={!!user?.id && comment.authorId === user.id}
                      isDeleting={deletingCommentId === comment.id}
                      onDelete={() => deleteComment(comment)}
                      onReply={
                        isAuthenticated
                          ? () => {
                              setReplyTo(comment);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          : undefined
                      }
                    />
                    {(replies.length > 0 || hasMoreReplies) && (
                      <div className="ml-6 sm:ml-10 mt-2 flex flex-col gap-2 border-l border-zinc-800 pl-4">
                        {replies.map((reply) => (
                          <CommentCard
                            key={reply.id}
                            comment={reply}
                            isReply
                            canDelete={!!user?.id && reply.authorId === user.id}
                            isDeleting={deletingCommentId === reply.id}
                            onDelete={() => deleteComment(reply)}
                            onReply={
                              isAuthenticated
                                ? () => {
                                    setReplyTo(reply);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }
                                : undefined
                            }
                          />
                        ))}
                        {hasMoreReplies && (
                          <button
                            type="button"
                            onClick={() => loadMoreReplies(comment)}
                            disabled={loadingRepliesFor === comment.id}
                            className="self-start inline-flex items-center gap-1 text-xs text-[#3fced1] hover:text-[#35b5b8] py-1 disabled:opacity-60"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                            {loadingRepliesFor === comment.id
                              ? 'Antworten werden geladen …'
                              : `Alle ${comment.replyCount} Antworten anzeigen`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CommentCard: React.FC<{
  comment: SetupComment;
  isReply?: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  onReply?: () => void;
}> = ({ comment, isReply = false, canDelete, isDeleting, onDelete, onReply }) => {
  const avatarUrl = communityService.buildThumbnailUrl(comment.authorAvatarUrl, 100);
  return (
    <div
      className={`rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 ${
        isReply ? '' : 'sm:p-4'
      }`}
    >
      <div className="flex items-start gap-3">
        <Avatar
          src={avatarUrl ?? undefined}
          showFallback
          fallback={<UserIcon size={14} className="text-zinc-500" />}
          className={`bg-zinc-800 shrink-0 ${isReply ? 'h-7 w-7' : 'h-8 w-8'}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <RouterLink
              to={`/spieler/${encodeURIComponent(comment.authorName)}`}
              className="text-sm font-semibold text-white hover:text-[#3fced1]"
            >
              {comment.authorName}
            </RouterLink>
            <span className="text-[11px] text-zinc-500">
              {communityService.formatRelativeTime(comment.createdAt)}
            </span>
          </div>
          <p className="mt-1 text-sm text-zinc-300 whitespace-pre-wrap break-words">
            {comment.content}
          </p>
          <div className="mt-2 flex items-center gap-3">
            {onReply && (
              <button
                type="button"
                onClick={onReply}
                className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-[#3fced1]"
              >
                <Reply className="w-3.5 h-3.5" />
                Antworten
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={onDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-red-400 disabled:opacity-60"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {isDeleting ? 'Wird gelöscht …' : 'Löschen'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupDetailPage;
